import { legalOwner as o } from './legalOwner';

export type LegalDocumentId = 'privacy' | 'terms' | 'notice';

/** A paragraph, or a bullet list. */
type Block = string | string[];

interface LegalDocument {
  title: string;
  sections: { heading: string; body: Block[] }[];
}

type LegalDocuments = Record<LegalDocumentId, LegalDocument> & { updated: string };

// Legal texts are whole documents per language (not UI strings), so they live here and not in
// the i18n catalogs.

const ownerEs = [
  `Titular: ${o.name}`,
  ...(o.taxId ? [`NIF: ${o.taxId}`] : []),
  ...(o.address ? [`Domicilio: ${o.address}`] : []),
  `Email de contacto: ${o.email}`,
  `Sitio web: ${o.website}`,
];
const ownerEn = [
  `Owner: ${o.name}`,
  ...(o.taxId ? [`Tax ID (NIF): ${o.taxId}`] : []),
  ...(o.address ? [`Address: ${o.address}`] : []),
  `Contact email: ${o.email}`,
  `Website: ${o.website}`,
];

const es: LegalDocuments = {
  updated: 'Última actualización:',
  privacy: {
    title: 'Política de privacidad',
    sections: [
      {
        heading: '1. Quién es el responsable',
        body: [
          'Nexo es un servicio gratuito de chat, tareas, sprints y llamadas para equipos. El responsable del tratamiento de tus datos es:',
          ownerEs,
        ],
      },
      {
        heading: '2. Qué datos tratamos',
        body: [
          [
            'Datos de la cuenta: email, nombre, cargo, foto (si pones una URL), idioma, zona horaria y preferencias de avisos.',
            'Contenido que creas: mensajes, reacciones, tareas, sprints, reuniones, archivos y comentarios que envías a tus equipos.',
            'Datos técnicos de seguridad: sesiones abiertas (dispositivo, navegador o app, dirección IP y fechas de uso).',
            'Llamadas: el audio y el vídeo se transmiten en directo entre los participantes y no se graban, salvo que alguien active el «Resumen con IA» (ver más abajo).',
            'Resumen con IA de una llamada (solo si alguien lo activa, con aviso visible para todos los participantes): el audio se envía para transcribirlo, la transcripción se usa para escribir el resumen que se publica en la conversación, y ni el audio ni la transcripción se guardan.',
            'Visitas a la web sin cuenta: un identificador aleatorio en tu navegador para contar cuántas personas hay conectadas. No se usa para identificarte ni se cruza con otros datos.',
          ],
        ],
      },
      {
        heading: '3. Para qué y con qué base legal',
        body: [
          [
            'Prestarte el servicio (cuenta, equipos, mensajes, llamadas, avisos y resúmenes por email): ejecución del contrato que aceptas al registrarte (art. 6.1.b RGPD).',
            'Proteger el servicio y tu cuenta (sesiones, límites de intentos, prevención de abusos): interés legítimo (art. 6.1.f RGPD).',
            'Atender errores, ideas y peticiones que nos envías: ejecución del contrato e interés legítimo.',
            'Cumplir obligaciones legales cuando una autoridad lo requiera (art. 6.1.c RGPD).',
          ],
          'No vendemos tus datos, no mostramos publicidad y no usamos tu contenido para entrenar modelos de inteligencia artificial.',
        ],
      },
      {
        heading: '4. Cuánto tiempo los guardamos',
        body: [
          'Mientras tengas la cuenta. Si la eliminas, borramos al momento tu perfil, sesiones, notificaciones, reacciones, guardados y ajustes. Los mensajes y tareas que escribiste en un equipo se quedan en ese equipo como «Cuenta eliminada», porque forman parte del trabajo del resto de personas. Las copias de seguridad cifradas se renuevan y desaparecen en un máximo de 30 días.',
        ],
      },
      {
        heading: '5. Quién más trata datos por nosotros',
        body: [
          'Usamos proveedores que actúan como encargados del tratamiento, con contrato y solo para prestar el servicio:',
          [
            'Cloudflare: red de distribución, protección y almacenamiento de archivos. Los archivos se cifran antes de guardarse, cada uno con su propia clave.',
            'Hostemy: envío de emails (invitaciones, recuperación de contraseña y resúmenes).',
            'EventSocket: entrega de avisos en tiempo real (solo identificadores y eventos, no el contenido de los archivos).',
            'OpenAI (transcripción) y Anthropic (redacción del resumen): solo cuando alguien activa el «Resumen con IA» en una llamada. Con sus condiciones para empresas, no usan estos datos para entrenar sus modelos.',
            'Nuestro proveedor de servidores, donde están la base de datos y el servidor de llamadas.',
          ],
          'Algunos de estos proveedores pueden tratar datos fuera del Espacio Económico Europeo; en ese caso lo hacen con las garantías del RGPD (cláusulas contractuales tipo de la Comisión Europea o el Marco de Privacidad de Datos UE-EE. UU.).',
        ],
      },
      {
        heading: '6. Tus derechos',
        body: [
          'Puedes ejercer en cualquier momento tus derechos de acceso, rectificación, supresión, portabilidad, limitación y oposición:',
          [
            'Desde tu perfil, en «Tus datos y privacidad», puedes descargar una copia de tus datos y eliminar tu cuenta tú mismo.',
            `Para cualquier otra petición, escríbenos a ${o.email}. Respondemos en un máximo de un mes.`,
            'Si crees que no hemos tratado bien tus datos, puedes reclamar ante la Agencia Española de Protección de Datos (www.aepd.es).',
          ],
        ],
      },
      {
        heading: '7. Seguridad',
        body: [
          'Las conexiones van cifradas (HTTPS), las contraseñas se guardan con argon2id, los archivos se cifran con AES-256-GCM y puedes ver y cerrar tus sesiones abiertas desde el perfil.',
        ],
      },
      {
        heading: '8. Cookies y almacenamiento local',
        body: [
          'Nexo no usa cookies de publicidad ni de analítica de terceros. Solo guarda en tu navegador lo necesario para funcionar: tu sesión, el idioma, el tema y el identificador anónimo de visita del apartado 2. Al ser técnicos y necesarios, no requieren consentimiento.',
        ],
      },
      {
        heading: '9. Menores',
        body: ['Para crear una cuenta debes tener al menos 14 años.'],
      },
      {
        heading: '10. Cambios',
        body: [
          'Si cambiamos esta política de forma importante te avisaremos dentro de la app o por email antes de que se aplique.',
        ],
      },
    ],
  },
  terms: {
    title: 'Términos de uso',
    sections: [
      {
        heading: '1. El servicio',
        body: [
          `Nexo (${o.website}) es un servicio de comunicación y organización para equipos, ofrecido por ${o.name}. Al crear una cuenta aceptas estos términos.`,
          'De momento Nexo es gratuito. Si en el futuro hay planes de pago, se avisará con antelación y nunca se cobrará nada sin tu aceptación expresa.',
        ],
      },
      {
        heading: '2. Tu cuenta',
        body: [
          'Debes dar un email real, tener al menos 14 años y mantener tu contraseña en secreto. Eres responsable de lo que se haga desde tu cuenta.',
        ],
      },
      {
        heading: '3. Tu contenido',
        body: [
          'Lo que escribes y subes sigue siendo tuyo o de tu equipo. Solo nos das permiso para guardarlo, mostrarlo a quien tenga acceso y procesarlo lo necesario para prestar el servicio.',
          'Los propietarios y administradores de cada equipo deciden quién entra y qué puede ver o hacer cada persona.',
        ],
      },
      {
        heading: '4. Uso aceptable',
        body: [
          'No está permitido usar Nexo para:',
          [
            'Actividades ilegales, acoso, amenazas, odio o contenido que explote a menores.',
            'Spam, fraude, suplantación de identidad o distribución de malware.',
            'Intentar acceder a cuentas o datos ajenos, saturar el servicio o saltarse sus límites.',
            'Infringir derechos de propiedad intelectual de terceros.',
          ],
          'Podemos suspender cuentas o equipos que incumplan estas normas, avisando siempre que sea posible.',
        ],
      },
      {
        heading: '5. Disponibilidad y responsabilidad',
        body: [
          'Trabajamos para que Nexo funcione bien y guardamos copias de seguridad, pero el servicio se ofrece «tal cual» y puede tener interrupciones o errores. En la medida que lo permita la ley, no respondemos de daños indirectos ni de la pérdida de contenido que no se pudiera evitar.',
        ],
      },
      {
        heading: '6. Darte de baja',
        body: [
          'Puedes eliminar tu cuenta cuando quieras desde tu perfil. También podemos dejar de ofrecer el servicio avisando con al menos 30 días para que puedas descargar tus datos.',
        ],
      },
      {
        heading: '7. Ley aplicable',
        body: [
          'Estos términos se rigen por la ley española. Si eres consumidor, puedes acudir a los tribunales de tu domicilio.',
        ],
      },
    ],
  },
  notice: {
    title: 'Aviso legal',
    sections: [
      {
        heading: 'Titular del sitio',
        body: [
          'En cumplimiento de la Ley 34/2002, de servicios de la sociedad de la información y de comercio electrónico (LSSI):',
          ownerEs,
        ],
      },
      {
        heading: 'Propiedad intelectual',
        body: [
          'El nombre Nexo, el logotipo, el diseño y el código de la web y las apps pertenecen a su titular. El contenido que publican los usuarios es de sus autores.',
        ],
      },
      {
        heading: 'Más información',
        body: [
          'El tratamiento de datos personales se explica en la Política de privacidad y las condiciones del servicio en los Términos de uso.',
        ],
      },
    ],
  },
};

const en: LegalDocuments = {
  updated: 'Last updated:',
  privacy: {
    title: 'Privacy policy',
    sections: [
      {
        heading: '1. Who is responsible',
        body: [
          'Nexo is a free chat, tasks, sprints and calls service for teams. The controller of your data is:',
          ownerEn,
        ],
      },
      {
        heading: '2. What data we process',
        body: [
          [
            'Account data: email, name, job title, photo (if you set a URL), language, time zone and notification preferences.',
            'Content you create: messages, reactions, tasks, sprints, meetings, files and comments you send to your teams.',
            'Security data: open sessions (device, browser or app, IP address and dates of use).',
            'Calls: audio and video are streamed live between participants and are not recorded, unless someone turns on the «AI summary» (see below).',
            'AI summary of a call (only if someone turns it on, with a notice every participant sees): the audio is sent to be transcribed, the transcript is used to write the summary posted in the conversation, and neither the audio nor the transcript is kept.',
            'Website visits without an account: a random identifier in your browser to count how many people are online. It is not used to identify you or combined with other data.',
          ],
        ],
      },
      {
        heading: '3. Why, and on what legal basis',
        body: [
          [
            'Providing the service (account, teams, messages, calls, alerts and email summaries): performance of the contract you accept when signing up (Art. 6(1)(b) GDPR).',
            'Protecting the service and your account (sessions, attempt limits, abuse prevention): legitimate interest (Art. 6(1)(f) GDPR).',
            'Handling the bug reports, ideas and requests you send us: contract and legitimate interest.',
            'Meeting legal obligations when an authority requires it (Art. 6(1)(c) GDPR).',
          ],
          "We don't sell your data, show ads or use your content to train artificial intelligence models.",
        ],
      },
      {
        heading: '4. How long we keep it',
        body: [
          "For as long as you have an account. If you delete it, your profile, sessions, notifications, reactions, saved items and settings are erased right away. Messages and tasks you wrote in a team stay in that team as «Deleted account», because they are part of other people's work. Encrypted backups rotate and disappear within 30 days.",
        ],
      },
      {
        heading: '5. Who else processes data for us',
        body: [
          'We use providers acting as processors, under contract and only to run the service:',
          [
            'Cloudflare: content delivery, protection and file storage. Files are encrypted before being stored, each with its own key.',
            'Hostemy: sending emails (invitations, password recovery and summaries).',
            'EventSocket: delivering real-time updates (identifiers and events only, not file contents).',
            'OpenAI (transcription) and Anthropic (writing the summary): only when someone turns on the «AI summary» in a call. Under their business terms, they do not use this data to train their models.',
            'Our server provider, which hosts the database and the call server.',
          ],
          'Some of these providers may process data outside the European Economic Area; when they do, it is under GDPR safeguards (European Commission standard contractual clauses or the EU-US Data Privacy Framework).',
        ],
      },
      {
        heading: '6. Your rights',
        body: [
          'You can exercise your rights of access, rectification, erasure, portability, restriction and objection at any time:',
          [
            'In your profile, under «Your data and privacy», you can download a copy of your data and delete your account yourself.',
            `For anything else, write to ${o.email}. We answer within one month.`,
            'If you think we have mishandled your data, you can complain to the Spanish Data Protection Agency (www.aepd.es) or your local authority.',
          ],
        ],
      },
      {
        heading: '7. Security',
        body: [
          'Connections are encrypted (HTTPS), passwords are stored with argon2id, files are encrypted with AES-256-GCM and you can see and close your open sessions from your profile.',
        ],
      },
      {
        heading: '8. Cookies and local storage',
        body: [
          "Nexo doesn't use advertising or third-party analytics cookies. It only stores what it needs to work in your browser: your session, language, theme and the anonymous visit identifier from section 2. Being technical and necessary, they don't require consent.",
        ],
      },
      {
        heading: '9. Children',
        body: ['You must be at least 14 years old to create an account.'],
      },
      {
        heading: '10. Changes',
        body: [
          "If we change this policy in an important way, we'll tell you in the app or by email before it applies.",
        ],
      },
    ],
  },
  terms: {
    title: 'Terms of use',
    sections: [
      {
        heading: '1. The service',
        body: [
          `Nexo (${o.website}) is a communication and organisation service for teams, provided by ${o.name}. By creating an account you accept these terms.`,
          'Nexo is free for now. If paid plans arrive in the future, they will be announced in advance and nothing will ever be charged without your express agreement.',
        ],
      },
      {
        heading: '2. Your account',
        body: [
          'You must use a real email, be at least 14 years old and keep your password secret. You are responsible for what is done from your account.',
        ],
      },
      {
        heading: '3. Your content',
        body: [
          "What you write and upload remains yours or your team's. You only allow us to store it, show it to whoever has access and process it as needed to provide the service.",
          "Each team's owners and admins decide who joins and what each person can see or do.",
        ],
      },
      {
        heading: '4. Acceptable use',
        body: [
          'You may not use Nexo for:',
          [
            'Illegal activity, harassment, threats, hate or content that exploits minors.',
            'Spam, fraud, impersonation or distributing malware.',
            "Trying to access other people's accounts or data, overloading the service or getting around its limits.",
            "Infringing third parties' intellectual property.",
          ],
          'We may suspend accounts or teams that break these rules, with notice whenever possible.',
        ],
      },
      {
        heading: '5. Availability and liability',
        body: [
          'We work to keep Nexo running well and keep backups, but the service is provided "as is" and may have outages or bugs. To the extent the law allows, we are not liable for indirect damages or for loss of content that could not be prevented.',
        ],
      },
      {
        heading: '6. Leaving',
        body: [
          "You can delete your account whenever you want from your profile. We may also stop offering the service with at least 30 days' notice so you can download your data.",
        ],
      },
      {
        heading: '7. Governing law',
        body: [
          'These terms are governed by Spanish law. If you are a consumer, you can go to the courts where you live.',
        ],
      },
    ],
  },
  notice: {
    title: 'Legal notice',
    sections: [
      {
        heading: 'Site owner',
        body: [
          'As required by Spanish Law 34/2002 on information society services and e-commerce (LSSI):',
          ownerEn,
        ],
      },
      {
        heading: 'Intellectual property',
        body: [
          'The Nexo name, logo, design and the code of the website and apps belong to their owner. Content published by users belongs to its authors.',
        ],
      },
      {
        heading: 'More information',
        body: [
          'How personal data is processed is explained in the Privacy policy, and the conditions of the service in the Terms of use.',
        ],
      },
    ],
  },
};

export const legalDocuments = { es, en };
